import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { useUpdateActorStatusMutation } from "Actors/Api/Mutations/useUpdateActorStatusMutation";
import { useActorDetailQuery } from "Actors/Api/Queries/useActorDetailQuery";
import { ActorCollaborationChip } from "Actors/Components/ActorCollaborationChip";
import { ActorDocumentsSection } from "Actors/Components/ActorDocumentsSection";
import { ActorInformation } from "Actors/Components/ActorInformation";
import { ActorPriceCategoriesSection } from "Actors/Components/ActorPriceCategoriesSection";
import { ActorVoicesSection } from "Actors/Components/ActorVoicesSection";
import { EditActorModal } from "Actors/Components/EditActorModal";
import { ActorStatus } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { UserAvatar } from "Shared/Avatar/UserAvatar";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";

export const ActorDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const { actorId } = useParams<{ actorId: string }>();
  const [isEditActorModalOpen, setIsEditActorModalOpen] = useState(false);

  const { data: actor, isLoading } = useActorDetailQuery(actorId);
  const updateActorStatusMutation = useUpdateActorStatusMutation();

  const handleStatusChange = async () => {
    if (!actorId || !actor) return;

    const newStatus =
      actor.status === ActorStatus.Active
        ? ActorStatus.Inactive
        : ActorStatus.Active;

    try {
      await updateActorStatusMutation.mutateAsync({
        actorID: actorId,
        status: newStatus,
      });

      toast.success(
        newStatus === ActorStatus.Active
          ? t(Resources.Actors.ActorDetail.ActivateSuccess)
          : t(Resources.Actors.ActorDetail.DeactivateSuccess),
      );
    } catch {
      toast.error(t(Resources.Actors.ActorDetail.StatusChangeError));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Actors)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!actor) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Actors)}>
        <Typography>{t(Resources.Actors.ActorDetail.NotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  const fullName = `${actor.firstName} ${actor.lastName}`;

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToActors)}
          defaultPath={AppRouting.Actors.path()}
        />
      }
      title={fullName}
      titleStartAdornment={
        <UserAvatar
          firstName={actor.firstName}
          lastName={actor.lastName}
          size={48}
          imageUrl={actor.profilePicture}
        />
      }
      titleEndAdornment={
        <ActorCollaborationChip
          isOpenToCollaboration={actor.isOpenToCollaboration}
        />
      }
      action={
        <Button
          variant="contained"
          color={actor.status === ActorStatus.Active ? "error" : "success"}
          onClick={handleStatusChange}
          disabled={updateActorStatusMutation.isPending}
        >
          {actor.status === ActorStatus.Active
            ? t(Resources.Actors.ActorDetail.Deactivate)
            : t(Resources.Actors.ActorDetail.Activate)}
        </Button>
      }
    >
      <Grid container spacing={Spacing.M}>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <ActorInformation
              actor={actor}
              onEdit={() => setIsEditActorModalOpen(true)}
            />
            <ActorDocumentsSection actorID={actorId!} />
          </Stack>
        </Grid>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <ActorVoicesSection actorID={actorId!} />
            <ActorPriceCategoriesSection actorID={actorId!} />
          </Stack>
        </Grid>
      </Grid>

      <EditActorModal
        isOpen={isEditActorModalOpen}
        onClose={() => setIsEditActorModalOpen(false)}
        actor={actor}
        actorID={actorId!}
      />
    </AuthenticatedLayout>
  );
};
